import { ReactNode, useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Modal, toastManager } from '@systemeio/components'
import { XCloseIcon, Download01Icon, ArrowNarrowRightIcon } from '@systemeio/icons'
import useEmblaCarousel from 'embla-carousel-react'
import { useTranslation } from 'next-i18next'
import { downloadFileFromBlob } from 'shared/utils/file/download-file'
import { getAttachmentFilename } from 'shared/utils/file/get-filename'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import { filterNotGalleryAttachments } from 'modules/attachments/utils/attachment-utils'
import { galleryFactory } from 'modules/community/components/attachment-gallery/gallery-factory'
import { AttachmentTypeEnum } from 'modules/community/types/post-attachment'

export const AttachmentGallery = (): ReactNode => {
  const { t } = useTranslation()
  const { attachments, initialGallerySlide, setIsGalleryVisible } = usePostAttachmentsContext()

  const [emblaRef, emblaApi] = useEmblaCarousel({
    axis: 'x',
    startIndex: initialGallerySlide || 0,
    dragFree: false,
    containScroll: 'trimSnaps',
  })

  const [selectedIndex, setSelectedIndex] = useState(initialGallerySlide || 0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const filteredAttachments = filterNotGalleryAttachments(attachments ?? [])
  const activeAttachment = filteredAttachments[selectedIndex]
  const canDownload =
    activeAttachment?.type === AttachmentTypeEnum.Image ||
    activeAttachment?.type === AttachmentTypeEnum.EmbeddedGif

  const updateSelection = useCallback(() => {
    if (!emblaApi) return

    setSelectedIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    updateSelection()
    emblaApi.on('select', updateSelection)
    emblaApi.on('reInit', updateSelection)

    return () => {
      emblaApi.off('select', updateSelection)
      emblaApi.off('reInit', updateSelection)
    }
  }, [emblaApi, updateSelection])

  // native download only works for same-origin uris
  const handleDownload = async (): Promise<void> => {
    if (!activeAttachment?.url) {
      toastManager.add({ type: 'error', title: t('core.error.internal_error_message') })
      return
    }

    try {
      const response = await fetch(activeAttachment.url, {
        headers: {
          Accept: '*/*',
        },
      })
      if (!response.ok) {
        Sentry.captureMessage('Error downloading attachment', {
          level: 'error',
          extra: {
            url: activeAttachment.url,
            status: response.status,
          },
        })
        return
      }

      const blob = await response.blob()
      const contentDisposition = response.headers.get('content-disposition')
      const filename = getAttachmentFilename(activeAttachment.url, {
        contentDisposition,
        contentType: blob.type,
      })

      downloadFileFromBlob(blob, filename)
    } catch (error) {
      Sentry.captureMessage('Error downloading attachment', {
        level: 'error',
        extra: {
          url: activeAttachment.url,
          error: error instanceof Error ? error.message : 'Unknown error',
          errorObject: error,
        },
      })
    }
  }

  return (
    <Modal.Root open onOpenChange={open => setIsGalleryVisible(open)}>
      <Modal.Popup
        aria-label={t('post.attachments.gallery', { defaultValue: 'Attachment gallery' })}
        showCloseButton={false}
        backdropProps={{ className: 'z-1001' }}
        viewportProps={{ className: 'z-1001 p-0' }}
        className="h-dvh max-h-none max-w-none rounded-none border-0 bg-black"
        onKeyDown={event => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault()
            scrollPrev()
          } else if (event.key === 'ArrowRight') {
            event.preventDefault()
            scrollNext()
          }
        }}
      >
        <div className="h-full overflow-hidden" ref={emblaRef}>
          <div className="flex h-full">
            {filteredAttachments?.map(attachment => (
              <div key={attachment.id} className="min-w-0 flex-[0_0_100%]">
                {galleryFactory(attachment)}
              </div>
            ))}
          </div>
        </div>

        <div>
          {canScrollPrev && (
            <button
              aria-label={t('global.previous')}
              className="absolute top-1/2 left-3 -translate-y-1/2 lg:left-12"
              onClick={scrollPrev}
            >
              <ArrowNarrowRightIcon className="size-5 rotate-180 stroke-text-on" />
            </button>
          )}
          {canScrollNext && (
            <button
              aria-label={t('global.next')}
              className="absolute top-1/2 right-3 -translate-y-1/2 lg:right-12"
              onClick={scrollNext}
            >
              <ArrowNarrowRightIcon className="size-5 stroke-text-on" />
            </button>
          )}
        </div>

        <div className="absolute top-3 right-3 inline-flex items-center gap-8 lg:top-12 lg:right-12">
          {canDownload && (
            <button
              type="button"
              aria-label={t('global.download', { defaultValue: 'Download' })}
              onClick={handleDownload}
            >
              <Download01Icon className="cursor-pointer text-text-on" height={21} width={21} />
            </button>
          )}
          <Modal.Close aria-label={t('global.close', { defaultValue: 'Close' })}>
            <XCloseIcon className="cursor-pointer text-text-on" height={25} width={25} />
          </Modal.Close>
        </div>
      </Modal.Popup>
    </Modal.Root>
  )
}
